/* eslint-disable no-console */
import { useCallback, useEffect, useMemo } from 'react';
import {
  APP_INSIGHTS_EVENT_TYPE,
  CustomEventProperties,
  identifyTelemetryUser,
  trackCustomEvent,
} from 'common/utils/telemetry';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { selectUser } from 'common/store/slices/auth.slice';
import { useSelector } from 'react-redux';

interface HookTrackEventArgs {
  type: APP_INSIGHTS_EVENT_TYPE;
  name: string;
  properties?: { [key: string]: any };
}

interface UseAppInsights {
  additionalTrackingInfo: CustomEventProperties;
  trackEvent: (event: HookTrackEventArgs) => void;
}

export const useAppInsights = (): UseAppInsights => {
  const user = useSelector(selectUser);
  const { activeProject } = useModuleProjectContext();
  const additionalTrackingInfo = useMemo(
    (): CustomEventProperties => ({
      userId: user.id ?? 'No user id available',
      email: user.email ?? 'No email available',
      companyName: user.companyName ?? 'No company name available',
      projectId: activeProject?.moduleProjectID ?? 'No project id available',
    }),
    [user, activeProject]
  );

  // Keep the GlitchTip scope identified with the authenticated user so
  // unhandled errors carry user context even without an explicit trackEvent.
  useEffect(() => {
    identifyTelemetryUser({ id: user.id, email: user.email });
  }, [user.id, user.email]);

  const trackEvent = useCallback(
    ({ type, name, properties = {} }: HookTrackEventArgs) => {
      trackCustomEvent({
        type,
        name,
        properties: {
          ...additionalTrackingInfo,
          ...properties,
        },
      });
    },
    [additionalTrackingInfo]
  );

  return { trackEvent, additionalTrackingInfo };
};
